import { weightNumber, type TrendPoint, type WeightUnit } from '@tahi/core';
import { copy } from '../copy';
import { shortDate } from '../format';

const W = 320;
const H = 140;
const PAD = 10;

/**
 * Daily weigh-ins as dots, the 7-day moving average as a line (trend, not noise), and the goal as a
 * dashed line. The numbers are in text on the same screen, so the chart is one labelled image.
 */
export function TrendChart({
  points,
  unit,
  goalG,
  label,
  compact = false,
}: {
  points: TrendPoint[];
  unit: WeightUnit;
  goalG: number | null;
  label: string;
  compact?: boolean;
}) {
  const values = points.flatMap((p) => [p.valueG, p.avgG]).filter((v): v is number => v !== null);
  if (values.length === 0) return <p className="text-sm text-muted">{copy.noTrend}</p>;
  const withGoal = goalG !== null ? [...values, goalG] : values;
  let max = Math.max(...withGoal);
  let min = Math.min(...withGoal);
  // At least 1 kg of range, so a flat week doesn't fill the chart with noise.
  if (max - min < 1000) {
    const mid = (max + min) / 2;
    max = mid + 500;
    min = mid - 500;
  }
  const h = compact ? 72 : H;
  const w = compact ? 200 : W;
  const x = (i: number) =>
    points.length === 1 ? w / 2 : PAD + (i * (w - 2 * PAD)) / (points.length - 1);
  const y = (v: number) => PAD + ((max - v) * (h - 2 * PAD)) / (max - min);

  // The average line breaks where there's no average (a week with no weigh-ins).
  let path = '';
  let open = false;
  points.forEach((p, i) => {
    if (p.avgG === null) {
      open = false;
      return;
    }
    path += `${open ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.avgG).toFixed(1)} `;
    open = true;
  });
  const first = points[0]!;
  const last = points[points.length - 1]!;

  return (
    <figure role="img" aria-label={label} className="flex flex-col gap-1">
      <div className="flex gap-2">
        {compact ? null : (
          <div
            aria-hidden="true"
            className="flex w-10 shrink-0 flex-col justify-between py-1 text-right font-mono text-[11px] tabular-nums text-muted"
          >
            <span>{weightNumber(max, unit)}</span>
            <span>{weightNumber(min, unit)}</span>
          </div>
        )}
        <svg
          viewBox={`0 0 ${w} ${h}`}
          className="min-w-0 flex-1"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <line x1={0} x2={w} y1={h - 1} y2={h - 1} stroke="var(--divider)" strokeWidth={1} />
          {goalG !== null ? (
            <line
              x1={0}
              x2={w}
              y1={y(goalG)}
              y2={y(goalG)}
              stroke="var(--success)"
              strokeWidth={1.5}
              strokeDasharray="6 5"
            />
          ) : null}
          {points.map((p, i) =>
            p.valueG === null ? null : (
              <circle
                key={p.date}
                cx={x(i)}
                cy={y(p.valueG)}
                r={compact ? 2.5 : 3.5}
                fill="var(--chart-1)"
                opacity={0.7}
              />
            ),
          )}
          <path
            d={path}
            fill="none"
            stroke="var(--accent)"
            strokeWidth={compact ? 2.5 : 3}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      </div>
      {compact ? null : (
        <>
          <div aria-hidden="true" className="ml-12 flex justify-between text-[11px] text-muted">
            <span>{shortDate(first.date)}</span>
            <span>{shortDate(last.date)}</span>
          </div>
          <ul aria-hidden="true" className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
            <li className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-[var(--chart-1)]" />
              {copy.legendDaily}
            </li>
            <li className="flex items-center gap-1.5">
              <span className="inline-block h-[3px] w-4 rounded bg-accent" />
              {copy.legendAverage}
            </li>
            {goalG !== null ? (
              <li className="flex items-center gap-1.5">
                <span className="inline-block w-4 border-t-2 border-dashed border-[var(--success)]" />
                {copy.legendGoal}
              </li>
            ) : null}
          </ul>
        </>
      )}
    </figure>
  );
}
